import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Paperclip, Download } from "lucide-react";
import { toast } from "sonner";

export const AttachmentLink = ({ attachment, testId }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open || !attachment?.id) return;
    let active = true, blobUrl;
    setError(""); setUrl("");
    api.get(`/attachments/${attachment.id}`, { responseType: "blob" }).then(({ data }) => {
      blobUrl = URL.createObjectURL(data);
      if (active) setUrl(blobUrl); else URL.revokeObjectURL(blobUrl);
    }).catch(() => { if (active) setError("Lampiran gagal dimuat. Tutup dan coba kembali."); });
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [open, attachment?.id]);
  if (!attachment?.id) return <span className="text-slate-400" data-testid={`${testId}-empty`}>—</span>;
  const pdf = attachment.content_type === "application/pdf";
  return <>
    <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid={testId} className="text-emerald-700 h-8 px-2">
      <Paperclip className="w-3 h-3 mr-1"/> Lihat {pdf ? "PDF" : "Foto"}
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle className="break-all pr-6">{attachment.original_filename || "Lampiran transaksi"}</DialogTitle></DialogHeader>
        {error ? <p role="alert" data-testid={`${testId}-error`}>{error}</p> : !url ? <p data-testid={`${testId}-loading`}>Memuat lampiran...</p> : <>
          {pdf ? <iframe src={url} title="Lampiran PDF" className="w-full h-[60vh] border rounded" data-testid={`${testId}-pdf`}/> : <img src={url} alt="Bukti transaksi" className="max-h-[60vh] w-full object-contain" data-testid={`${testId}-image`}/>}
          <a href={url} download={pdf ? attachment.original_filename : `${(attachment.original_filename || "bukti").replace(/\.[^.]+$/, "")}.webp`} data-testid={`${testId}-download`} className="text-sm text-emerald-700 inline-flex items-center gap-2"><Download className="w-4 h-4"/> Unduh lampiran</a>
        </>}
      </DialogContent>
    </Dialog>
  </>;
};

export const AttachmentField = ({ kind, value, onChange, onBusyChange, disabled, testId }) => {
  const input = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const pdfAllowed = kind === "retail-report";
  const selectFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", ...(pdfAllowed ? ["application/pdf"] : [])];
    if (!allowed.includes(file.type) || file.size === 0 || file.size > 10 * 1024 * 1024) {
      setError(`Gunakan ${pdfAllowed ? "foto atau PDF" : "foto JPG, PNG, WebP"} dengan ukuran 1 byte–10 MB.`);
      event.target.value = ""; return;
    }
    setError(""); setProgress(0); onBusyChange(true);
    try {
      const { data: session } = await api.post("/attachments/uploads", { kind, filename: file.name, content_type: file.type, size: file.size });
      for (let offset = 0, index = 0; offset < file.size; offset += session.chunk_size, index++) {
        const chunk = file.slice(offset, offset + session.chunk_size);
        await api.put(`/attachments/uploads/${session.id}/chunks/${index}`, chunk, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (e) => setProgress(Math.min(90, Math.round((offset + e.loaded) / file.size * 90))),
        });
      }
      setProgress(95);
      const { data } = await api.post(`/attachments/uploads/${session.id}/complete`);
      onChange(data); toast.success("Lampiran siap. Simpan transaksi untuk menautkannya.");
    } catch (e) { setError(errMsg(e)); }
    finally { setProgress(null); onBusyChange(false); if (input.current) input.current.value = ""; }
  };
  return <div className="space-y-2" data-testid={`${testId}-field`}>
    <Label htmlFor={testId}>{pdfAllowed ? "Lampiran Laporan (Foto / PDF)" : "Bukti Transfer (Foto)"}</Label>
    <input ref={input} id={testId} type="file" accept={`image/jpeg,image/png,image/webp${pdfAllowed ? ",application/pdf" : ""}`} disabled={disabled || progress !== null} onChange={selectFile} data-testid={testId} className="block w-full text-xs file:mr-2 file:rounded file:border file:bg-white file:px-3 file:py-2"/>
    <p className="text-xs text-slate-500">Opsional, maksimal 10 MB. Foto otomatis dikompres.</p>
    {progress !== null && <div role="status" data-testid={`${testId}-progress`}><progress className="w-full accent-emerald-600" value={progress} max={100}/><p className="text-xs">{progress >= 95 ? "Memvalidasi dan menyimpan lampiran..." : `Mengunggah ${progress}%`}</p></div>}
    {error && <p className="text-xs text-red-600" role="alert" data-testid={`${testId}-error`}>{error}</p>}
    {value && <div className="flex flex-wrap items-center gap-2"><span className="text-xs break-all" data-testid={`${testId}-filename`}>{value.original_filename}</span><AttachmentLink attachment={value} testId={`${testId}-preview`}/><Button type="button" variant="ghost" size="sm" disabled={disabled || progress !== null} onClick={() => onChange(null)} data-testid={`${testId}-remove`}>Hapus lampiran</Button></div>}
  </div>;
};
